import { useState, useEffect } from 'react';
import { Shield, X } from 'lucide-react';
import { Button } from './ui/button';
import { grantConsent, denyConsent, getConsentStatus } from '../lib/analytics';
import { useLanguage } from '../context/LanguageContext';

const texts = {
  de: {
    title: 'Cookie-Einstellungen',
    desc: 'Wir verwenden Cookies und ähnliche Technologien, um Ihnen ein optimales Erlebnis zu bieten, den Datenverkehr zu analysieren und Werbung zu personalisieren. Weitere Informationen finden Sie in unserer Datenschutzerklärung.',
    accept: 'Alle akzeptieren',
    decline: 'Nur notwendige',
    manage: 'Einstellungen',
  },
  en: {
    title: 'Cookie Settings',
    desc: 'We use cookies and similar technologies to provide you with an optimal experience, analyze traffic and personalize advertising. For more information, see our Privacy Policy.',
    accept: 'Accept All',
    decline: 'Essential Only',
    manage: 'Settings',
  },
  tr: {
    title: 'Çerez Ayarları',
    desc: 'Size en iyi deneyimi sunmak, trafiği analiz etmek ve reklamları kişiselleştirmek için çerezler ve benzer teknolojiler kullanıyoruz. Daha fazla bilgi için Gizlilik Politikamıza bakın.',
    accept: 'Tümünü Kabul Et',
    decline: 'Sadece Gerekli',
    manage: 'Ayarlar',
  },
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const { language } = useLanguage();
  const t = texts[language] || texts.de;

  useEffect(() => {
    const status = getConsentStatus();
    if (!status) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    grantConsent();
    setVisible(false);
  };

  const handleDecline = () => {
    denyConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-4 animate-slide-up" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-[#111] border border-[#2a2a2a] rounded-2xl shadow-2xl shadow-black/50 p-6 backdrop-blur-xl">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-10 h-10 rounded-full bg-[#D4AF37]/10 items-center justify-center shrink-0 mt-0.5">
            <Shield className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-bold text-sm">{t.title}</h3>
              <button onClick={handleDecline} className="text-gray-500 hover:text-gray-300 transition-colors p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-gray-400 text-xs leading-relaxed mb-4">{t.desc}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={handleAccept}
                size="sm"
                data-testid="cookie-accept-btn"
                className="bg-gradient-to-r from-[#F1D27A] to-[#D4AF37] text-black font-bold px-6 py-2 text-xs rounded-lg hover:from-[#D4AF37] hover:to-[#8F6B1F] transition-all"
              >
                {t.accept}
              </Button>
              <Button
                onClick={handleDecline}
                size="sm"
                variant="outline"
                data-testid="cookie-decline-btn"
                className="border-[#2a2a2a] text-gray-400 hover:text-white hover:border-gray-500 px-6 py-2 text-xs rounded-lg transition-all"
              >
                {t.decline}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
